/**
 * Стек всплывающих слоёв (модалки, панели, шиты, тур). Единственное место,
 * где живут: блокировка скролла со счётчиком (NFR-7), закрытие по Esc и
 * кнопке «Назад» браузера — только верхним слоем (NAV-10).
 *
 * popstate и keydown слушаются один раз глобально: так «свой» history.back
 * при программном закрытии гарантированно поглощается, а не достаётся
 * следующему открытому слою.
 */
/** Чем слой закрыт: Esc или кнопка «Назад» — телеметрии нужна причина, а не факт. */
export type LayerCloseReason = 'escape' | 'back';

export interface LayerEntry {
  id: string;
  onClose: (reason: LayerCloseReason) => void;
  closeOnEscape: boolean;
  /** Слой без своей записи истории: адресом управляет роутер (`?video=`), popstate его не закрывает. */
  historyless?: boolean;
  /** Слой узнаёт, что его запись истории уже снята кнопкой «Назад». */
  markPopped: () => void;
}

let stack: LayerEntry[] = [];
let scrollLocks = 0;
let programmaticPops = 0;
let counter = 0;
let listenersInstalled = false;

export function nextLayerId(): string {
  counter += 1;
  return `layer_${counter}`;
}

function onPopstate(): void {
  if (programmaticPops > 0) {
    programmaticPops -= 1;
    return;
  }
  const top = stack[stack.length - 1];
  if (!top || top.historyless) return;
  top.markPopped();
  stack = stack.filter((entry) => entry !== top);
  top.onClose('back');
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  const top = stack[stack.length - 1];
  if (!top || !top.closeOnEscape) return;
  event.preventDefault();
  top.onClose('escape');
}

/**
 * Роутер (react-router) переписывает history.state при replaceState и стирал бы
 * маркер `layer`, по которому слой узнаёт свою запись истории. Маркер переносится
 * в новое состояние — так «Назад» после смены фильтров внутри шита всё ещё
 * закрывает шит, а не листает историю.
 */
function preserveLayerMarker(): void {
  const original = window.history.replaceState.bind(window.history);
  window.history.replaceState = (state: unknown, title: string, url?: string | URL | null) => {
    const previous = window.history.state as { layer?: string | null } | null;
    let next = state;
    if (previous?.layer) {
      if (state === null || state === undefined) next = { layer: previous.layer };
      // Явный `layer: null` снимает маркер — так слой оставляет запись истории роутеру.
      else if (typeof state === 'object' && !('layer' in (state as object)))
        next = { ...(state as Record<string, unknown>), layer: previous.layer };
    }
    original(next, title, url ?? null);
  };
}

function ensureListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  window.addEventListener('popstate', onPopstate);
  window.addEventListener('keydown', onKeydown);
  preserveLayerMarker();
}

/**
 * Снятие своей записи истории откладывается на тик: при немедленном
 * ремонтировании (StrictMode в dev) тот же слой отменяет back() и переиспользует
 * запись — иначе асинхронный back() и новый pushState путали бы историю.
 */
const pendingBacks = new Map<string, ReturnType<typeof setTimeout>>();

export function scheduleLayerBack(id: string): void {
  const timer = setTimeout(() => {
    pendingBacks.delete(id);
    expectProgrammaticPop();
    window.history.back();
  }, 0);
  pendingBacks.set(id, timer);
}

/**
 * Слой закрыт, но адрес за это время изменился (фильтры в шите): запись
 * истории остаётся роутеру, маркер снимается — «Назад» вернёт прежний адрес.
 */
export function releaseLayerEntry(): void {
  const state = (window.history.state as Record<string, unknown> | null) ?? {};
  window.history.replaceState({ ...state, layer: null }, '', window.location.href);
}

/** true — запись этого слоя ещё в истории (back отменён), pushState не нужен. */
export function reclaimLayerEntry(id: string): boolean {
  const timer = pendingBacks.get(id);
  if (!timer) return false;
  clearTimeout(timer);
  pendingBacks.delete(id);
  return true;
}

export function pushLayer(entry: LayerEntry): void {
  ensureListeners();
  stack = [...stack.filter((x) => x.id !== entry.id), entry];
}

export function removeLayer(id: string): void {
  stack = stack.filter((x) => x.id !== id);
}

export function isTopLayer(id: string): boolean {
  return stack[stack.length - 1]?.id === id;
}

export function lockScroll(): void {
  scrollLocks += 1;
  document.documentElement.setAttribute('data-scroll-locked', '');
}

export function unlockScroll(): void {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) document.documentElement.removeAttribute('data-scroll-locked');
}

/** Следующий popstate вызван нами (history.back при закрытии) — слои его не видят. */
export function expectProgrammaticPop(): void {
  programmaticPops += 1;
}

/** Для тестов: имитация popstate / Esc без реального окна. */
export const __internals = {
  onPopstate,
  onKeydown,
  pendingProgrammaticPops: () => programmaticPops,
};

/** Для тестов. */
export function resetLayerStack(): void {
  stack = [];
  scrollLocks = 0;
  programmaticPops = 0;
  for (const timer of pendingBacks.values()) clearTimeout(timer);
  pendingBacks.clear();
  document.documentElement.removeAttribute('data-scroll-locked');
}
